import type { Difficulty, DifficultyTuning, LevelConfig } from './types.js';

export const DIFFICULTY_TUNING: Record<Difficulty, DifficultyTuning> = {
  easy: {
    enemyHealth: 0.8,
    enemySpeed: 0.82,
    enemyFireRate: 0.78,
    spawnRate: 0.86,
    scoreMultiplier: 0.8,
  },
  normal: {
    enemyHealth: 1,
    enemySpeed: 1,
    enemyFireRate: 1,
    spawnRate: 1,
    scoreMultiplier: 1,
  },
  hard: {
    enemyHealth: 1.28,
    enemySpeed: 1.18,
    enemyFireRate: 1.3,
    spawnRate: 1.22,
    scoreMultiplier: 1.45,
  },
};

export const LEVELS: LevelConfig[] = [
  {
    id: 1,
    name: '奶油草原',
    subtitle: '彩虹核心防线',
    objective: '守住彩虹核心',
    description: '在更开阔的草原工事中击退 12 辆玩具坦克，争夺中心补给并利用侧翼反弹墙打出漂亮连击。',
    themeTag: '草原主题',
    arena: { width: 42, depth: 30 },
    corePosition: { x: 0, z: 10.8 },
    playerSpawns: [{ x: -3.1, z: 7.6 }, { x: 3.1, z: 7.6 }],
    enemySpawns: [{ x: -16.5, z: -12.2 }, { x: -5.4, z: -13.1 }, { x: 5.4, z: -13.1 }, { x: 16.5, z: -12.2 }],
    waves: [
      { label: '热身侦察', enemies: ['scout', 'scout', 'scout'], interval: 1.05, pauseAfter: 2.2 },
      { label: '侧翼夹击', enemies: ['scout', 'bruiser', 'scout', 'scout'], interval: 0.95, pauseAfter: 2.1 },
      { label: '核心冲锋', enemies: ['sniper', 'bruiser', 'scout', 'scout', 'scout'], interval: 0.82, pauseAfter: 1.4 },
    ],
    obstacles: [
      { x: -13.9, z: -5.9, width: 1.2, depth: 6.4, kind: 'metal' },
      { x: 13.9, z: -5.9, width: 1.2, depth: 6.4, kind: 'metal' },
      { x: -6.6, z: -4.1, width: 5.2, depth: 1.1, kind: 'metal', rotation: 0.18 },
      { x: 6.6, z: -4.1, width: 5.2, depth: 1.1, kind: 'metal', rotation: -0.18 },
      { x: 0, z: -6.6, width: 4.2, depth: 1.1, kind: 'metal' },
      { x: 0, z: -1.4, width: 3.1, depth: 1.25, kind: 'crate', health: 4 },
      { x: -8.6, z: 2.0, width: 3.0, depth: 1.15, kind: 'crate', health: 3 },
      { x: 8.6, z: 2.0, width: 3.0, depth: 1.15, kind: 'crate', health: 3 },
      { x: -4.8, z: 5.7, width: 2.6, depth: 1.1, kind: 'crate', health: 2 },
      { x: 4.8, z: 5.7, width: 2.6, depth: 1.1, kind: 'crate', health: 2 },
      { x: -16.8, z: 5.5, width: 1.1, depth: 5.2, kind: 'metal' },
      { x: 16.8, z: 5.5, width: 1.1, depth: 5.2, kind: 'metal' },
      { x: -10.8, z: 9.1, width: 3.6, depth: 1.05, kind: 'crate', health: 3 },
      { x: 10.8, z: 9.1, width: 3.6, depth: 1.05, kind: 'crate', health: 3 },
    ],
    pickupSpots: [
      { x: 0, z: -4.2, weight: 1.35, kinds: ['rapid', 'overdrive'] },
      { x: -12.7, z: -0.6, weight: 1.05, kinds: ['shield', 'mine'] },
      { x: 12.7, z: -0.6, weight: 1.05, kinds: ['shield', 'drone'] },
      { x: -15.2, z: 7.8, weight: 0.9, kinds: ['repair', 'mine'] },
      { x: 15.2, z: 7.8, weight: 0.9, kinds: ['repair', 'drone'] },
      { x: 0, z: 6.2, weight: 0.8, kinds: ['repair', 'shield'] },
    ],
    hazards: [
      { x: -11.8, z: 2.5, radius: 2.55, kind: 'mud', strength: 0.72 },
      { x: 11.8, z: 2.5, radius: 2.55, kind: 'mud', strength: 0.72 },
      { x: 0, z: -9.2, radius: 2.2, kind: 'spark', strength: 1 },
    ],
    palette: {
      sky: 0x8fe2ff,
      fog: 0xbceeff,
      groundA: '#8acb6f',
      groundB: '#9edb78',
      groundLine: '#d9f3a7',
      rim: 0xffe26b,
      accent: 0x43e0c3,
      danger: 0xff6d58,
    },
    scenery: 'meadow',
  },
  {
    id: 2,
    name: '积木荒漠',
    subtitle: '弹射峡谷伏击',
    objective: '穿过弹射峡谷',
    description: '在双层峡谷路线中击退 16 辆敌车，争夺暴露补给点并用金属墙改变炮弹路线。',
    themeTag: '荒漠主题',
    arena: { width: 44, depth: 32 },
    corePosition: { x: 0, z: 11.8 },
    playerSpawns: [{ x: -3.3, z: 8.4 }, { x: 3.3, z: 8.4 }],
    enemySpawns: [{ x: -18, z: -13 }, { x: -7, z: -14 }, { x: 7, z: -14 }, { x: 18, z: -13 }, { x: 0, z: -12.2 }],
    waves: [
      { label: '沙尘侦察', enemies: ['scout', 'scout', 'sniper'], interval: 0.95, pauseAfter: 2.0 },
      { label: '重甲车队', enemies: ['bruiser', 'scout', 'bruiser', 'scout'], interval: 0.86, pauseAfter: 1.9 },
      { label: '峡谷交叉火力', enemies: ['sniper', 'scout', 'sniper', 'scout'], interval: 0.78, pauseAfter: 1.8 },
      { label: '积木风暴', enemies: ['bruiser', 'scout', 'scout', 'bruiser', 'scout'], interval: 0.68, pauseAfter: 1.2 },
    ],
    obstacles: [
      { x: -15.6, z: -7.0, width: 1.25, depth: 8.4, kind: 'metal' },
      { x: 15.6, z: -7.0, width: 1.25, depth: 8.4, kind: 'metal' },
      { x: -7.2, z: -4.8, width: 6.3, depth: 1.15, kind: 'metal', rotation: 0.22 },
      { x: 7.2, z: -4.8, width: 6.3, depth: 1.15, kind: 'metal', rotation: -0.22 },
      { x: 0, z: -7.8, width: 4.4, depth: 1.1, kind: 'metal' },
      { x: -3.8, z: -0.6, width: 4.8, depth: 1.1, kind: 'metal', rotation: -0.14 },
      { x: 3.8, z: -0.6, width: 4.8, depth: 1.1, kind: 'metal', rotation: 0.14 },
      { x: -11.8, z: 3.6, width: 3.4, depth: 1.15, kind: 'crate', health: 4 },
      { x: 11.8, z: 3.6, width: 3.4, depth: 1.15, kind: 'crate', health: 4 },
      { x: -5.9, z: 5.4, width: 2.7, depth: 2.1, kind: 'crate', health: 3 },
      { x: 5.9, z: 5.4, width: 2.7, depth: 2.1, kind: 'crate', health: 3 },
      { x: 0, z: 3.0, width: 2.8, depth: 2.8, kind: 'crate', health: 5 },
      { x: -18.6, z: 5.6, width: 1.0, depth: 6.2, kind: 'metal' },
      { x: 18.6, z: 5.6, width: 1.0, depth: 6.2, kind: 'metal' },
      { x: -10.2, z: 10.3, width: 3.2, depth: 1.05, kind: 'crate', health: 3 },
      { x: 10.2, z: 10.3, width: 3.2, depth: 1.05, kind: 'crate', health: 3 },
    ],
    pickupSpots: [
      { x: 0, z: -5.0, weight: 1.4, kinds: ['overdrive', 'rapid'] },
      { x: -12.6, z: -1.0, weight: 1.1, kinds: ['shield', 'drone'] },
      { x: 12.6, z: -1.0, weight: 1.1, kinds: ['shield', 'mine'] },
      { x: -17.2, z: 7.8, weight: 0.95, kinds: ['repair', 'mine'] },
      { x: 17.2, z: 7.8, weight: 0.95, kinds: ['repair', 'drone'] },
      { x: -4.8, z: 9.2, weight: 0.75, kinds: ['repair', 'shield'] },
      { x: 4.8, z: 9.2, weight: 0.75, kinds: ['repair', 'shield'] },
    ],
    hazards: [
      { x: -9.6, z: -8.8, radius: 2.65, kind: 'spark', strength: 1 },
      { x: 9.6, z: -8.8, radius: 2.65, kind: 'spark', strength: 1 },
      { x: 0, z: 0.6, radius: 2.8, kind: 'mud', strength: 0.68 },
      { x: -14.6, z: 7.6, radius: 2.25, kind: 'mud', strength: 0.74 },
      { x: 14.6, z: 7.6, radius: 2.25, kind: 'mud', strength: 0.74 },
    ],
    palette: {
      sky: 0xffb86b,
      fog: 0xffc98c,
      groundA: '#dc9458',
      groundB: '#e9aa63',
      groundLine: '#ffe092',
      rim: 0xffdc5a,
      accent: 0x4ec9c2,
      danger: 0xe8494f,
    },
    scenery: 'desert',
  },
  {
    id: 3,
    name: '霓虹月堡',
    subtitle: '对决皇冠巨坦',
    objective: '击败皇冠巨坦',
    description: '突破多焦点月堡战场，利用危险能量池和高风险补给点击败会散射炮弹并召唤援军的皇冠巨坦。',
    themeTag: '月堡主题',
    arena: { width: 46, depth: 34 },
    corePosition: { x: 0, z: 12.8 },
    playerSpawns: [{ x: -3.5, z: 9.3 }, { x: 3.5, z: 9.3 }],
    enemySpawns: [{ x: -19, z: -14 }, { x: -8, z: -15 }, { x: 8, z: -15 }, { x: 19, z: -14 }, { x: 0, z: -13.4 }],
    waves: [
      { label: '月面巡逻', enemies: ['scout', 'sniper', 'scout', 'scout'], interval: 0.86, pauseAfter: 1.8 },
      { label: '霓虹重甲', enemies: ['bruiser', 'sniper', 'bruiser', 'scout'], interval: 0.78, pauseAfter: 1.8 },
      { label: '王座护卫', enemies: ['scout', 'bruiser', 'sniper', 'scout', 'bruiser'], interval: 0.68, pauseAfter: 2.0 },
      { label: '皇冠巨坦', enemies: ['boss'], interval: 0.4, pauseAfter: 1.0 },
    ],
    obstacles: [
      { x: -17.0, z: -7.2, width: 1.1, depth: 8.7, kind: 'metal' },
      { x: 17.0, z: -7.2, width: 1.1, depth: 8.7, kind: 'metal' },
      { x: -9.2, z: -5.4, width: 5.6, depth: 1.0, kind: 'metal', rotation: 0.13 },
      { x: 9.2, z: -5.4, width: 5.6, depth: 1.0, kind: 'metal', rotation: -0.13 },
      { x: -3.8, z: -1.8, width: 4.2, depth: 1.0, kind: 'metal' },
      { x: 3.8, z: -1.8, width: 4.2, depth: 1.0, kind: 'metal' },
      { x: 0, z: 1.5, width: 3.1, depth: 1.2, kind: 'crate', health: 5 },
      { x: -10.4, z: 4.8, width: 3.8, depth: 1.1, kind: 'crate', health: 4 },
      { x: 10.4, z: 4.8, width: 3.8, depth: 1.1, kind: 'crate', health: 4 },
      { x: -5.8, z: 7.2, width: 2.6, depth: 1.2, kind: 'crate', health: 3 },
      { x: 5.8, z: 7.2, width: 2.6, depth: 1.2, kind: 'crate', health: 3 },
      { x: -19.2, z: 5.6, width: 1.0, depth: 5.8, kind: 'metal' },
      { x: 19.2, z: 5.6, width: 1.0, depth: 5.8, kind: 'metal' },
      { x: -12.5, z: 11.3, width: 3.4, depth: 1.05, kind: 'crate', health: 3 },
      { x: 12.5, z: 11.3, width: 3.4, depth: 1.05, kind: 'crate', health: 3 },
      { x: 0, z: -9.4, width: 5.4, depth: 1.1, kind: 'metal' },
    ],
    pickupSpots: [
      { x: 0, z: -6.8, weight: 1.45, kinds: ['overdrive', 'drone'] },
      { x: -14.4, z: -1.2, weight: 1.1, kinds: ['rapid', 'mine'] },
      { x: 14.4, z: -1.2, weight: 1.1, kinds: ['rapid', 'drone'] },
      { x: -18.0, z: 8.5, weight: 0.95, kinds: ['repair', 'shield'] },
      { x: 18.0, z: 8.5, weight: 0.95, kinds: ['repair', 'shield'] },
      { x: -5.2, z: 10.2, weight: 0.8, kinds: ['shield', 'mine'] },
      { x: 5.2, z: 10.2, weight: 0.8, kinds: ['shield', 'drone'] },
      { x: 0, z: 4.4, weight: 0.7, kinds: ['repair', 'overdrive'] },
    ],
    hazards: [
      { x: -11.7, z: -8.4, radius: 2.55, kind: 'spark', strength: 1 },
      { x: 11.7, z: -8.4, radius: 2.55, kind: 'spark', strength: 1 },
      { x: 0, z: -3.4, radius: 3.0, kind: 'mud', strength: 0.66 },
      { x: -15.4, z: 6.7, radius: 2.45, kind: 'spark', strength: 1 },
      { x: 15.4, z: 6.7, radius: 2.45, kind: 'spark', strength: 1 },
    ],
    palette: {
      sky: 0x30265f,
      fog: 0x4a3774,
      groundA: '#343351',
      groundB: '#444361',
      groundLine: '#7270a5',
      rim: 0xffd85d,
      accent: 0x54f0ed,
      danger: 0xff4f83,
    },
    scenery: 'moon',
  },
];

export function totalEnemies(level: LevelConfig): number {
  return level.waves.reduce((sum, wave) => sum + wave.enemies.length, 0);
}

/**
 * 联机对战（PvP）竞技场：左右镜像对称（x → -x，旋转取反）。
 *
 * 独立常量导出，不进 LEVELS（关卡选择 UI 不出现）：
 * - playerSpawns = alpha 队（左侧），enemySpawns = bravo 队（右侧镜像），
 *   每项两个点位对应队内 slot 0/1（2v2；1v1 只用 slot 0）；
 * - 金属墙构成中央遮挡与侧翼掩体（配合战争迷雾挡视线），Crate 提供可破坏掩体；
 * - waves 为空（无波次 AI），corePosition 仅为满足关卡结构（pvp 不创建核心）。
 */
export const PVP_ARENA: LevelConfig = {
  id: 0,
  name: '对决竞技场',
  subtitle: '联机对战',
  objective: '消灭对方战队',
  description: '扩大重设计的联机竞技场：中央木箱群争夺高价值补给，金属墙构成侧翼走廊与绕后路线，腹地出生保护墙避免出生即被狙击。',
  themeTag: '竞技主题',
  arena: { width: 58, depth: 42 },
  corePosition: { x: 0, z: 0 },
  playerSpawns: [{ x: -24, z: -8 }, { x: -24, z: 8 }],
  enemySpawns: [{ x: 24, z: 8 }, { x: 24, z: -8 }],
  waves: [],
  obstacles: [
    // 中央争夺区：中央大木箱 + 中轴自对称金属墙（分隔中路南北通道）
    { x: 0, z: 0, width: 3.4, depth: 3.4, kind: 'crate', health: 5 },
    { x: 0, z: -8, width: 1.3, depth: 5, kind: 'metal' },
    { x: 0, z: 8, width: 1.3, depth: 5, kind: 'metal' },
    // 中轴端部金属柱（自对称，挡住边路走廊的中轴穿越口，制造路线抉择）
    { x: 0, z: -16, width: 4.2, depth: 1.2, kind: 'metal' },
    { x: 0, z: 16, width: 4.2, depth: 1.2, kind: 'metal' },
    // 中路 Crate 掩体（镜像对）
    { x: -4.6, z: 0, width: 2.6, depth: 1.2, kind: 'crate', health: 3 },
    { x: 4.6, z: 0, width: 2.6, depth: 1.2, kind: 'crate', health: 3 },
    // 内侧斜墙（镜像对，rotation 取反）：中路与边路之间的视线遮挡
    { x: -9, z: -6, width: 5.8, depth: 1.15, kind: 'metal', rotation: 0.32 },
    { x: 9, z: -6, width: 5.8, depth: 1.15, kind: 'metal', rotation: -0.32 },
    { x: -9, z: 6, width: 5.8, depth: 1.15, kind: 'metal', rotation: -0.32 },
    { x: 9, z: 6, width: 5.8, depth: 1.15, kind: 'metal', rotation: 0.32 },
    // 侧翼走廊长金属墙（镜像对）：构成掩体与绕后路线
    { x: -15.5, z: -13.5, width: 1.25, depth: 7, kind: 'metal' },
    { x: 15.5, z: -13.5, width: 1.25, depth: 7, kind: 'metal' },
    { x: -15.5, z: 13.5, width: 1.25, depth: 7, kind: 'metal' },
    { x: 15.5, z: 13.5, width: 1.25, depth: 7, kind: 'metal' },
    // 侧翼 Crate（镜像对）：可破坏掩体与视野开拓
    { x: -13, z: -9, width: 3.2, depth: 1.15, kind: 'crate', health: 4 },
    { x: 13, z: -9, width: 3.2, depth: 1.15, kind: 'crate', health: 4 },
    { x: -13, z: 9, width: 3.2, depth: 1.15, kind: 'crate', health: 4 },
    { x: 13, z: 9, width: 3.2, depth: 1.15, kind: 'crate', health: 4 },
    // 腹地出生保护墙（镜像对）：挡住出生点正前方的直射视线
    { x: -20, z: -8, width: 1.25, depth: 6.5, kind: 'metal' },
    { x: 20, z: -8, width: 1.25, depth: 6.5, kind: 'metal' },
    { x: -20, z: 8, width: 1.25, depth: 6.5, kind: 'metal' },
    { x: 20, z: 8, width: 1.25, depth: 6.5, kind: 'metal' },
    // 无人区中路 Crate（镜像对）：推进途中的可破坏掩体
    { x: -16.5, z: 0, width: 2.8, depth: 1.2, kind: 'crate', health: 3 },
    { x: 16.5, z: 0, width: 2.8, depth: 1.2, kind: 'crate', health: 3 },
  ],
  pickupSpots: [
    // 中央高价值点（中轴自对称）；2026-07-26 起 drone 回归 PvP 掉落池（弹道走 drone 事件复现、索敌受迷雾约束）
    { x: 0, z: -4.5, weight: 1.45, kinds: ['overdrive', 'rapid', 'drone'] },
    { x: 0, z: 4.5, weight: 1.4, kinds: ['overdrive', 'rapid', 'drone'] },
    // 中路争夺点（镜像对）：无人机加入争夺池；repair 补进中路（2026-07-26 平衡：
    // 3 血制下全场 repair 掉落占比仅 ~6.75% 且只在腹地低权重点，掉血后几乎无回复路线）
    { x: -11, z: 0, weight: 1.05, kinds: ['shield', 'mine', 'drone', 'repair'] },
    { x: 11, z: 0, weight: 1.05, kinds: ['shield', 'mine', 'drone', 'repair'] },
    // 侧翼进攻点（镜像对）：全进攻型
    { x: -17.5, z: -8.5, weight: 1.0, kinds: ['rapid', 'drone', 'mine'] },
    { x: 17.5, z: -8.5, weight: 1.0, kinds: ['rapid', 'drone', 'mine'] },
    // 腹地防守点（镜像对）：补充地雷用于布防出生侧要道
    { x: -17.5, z: 8.5, weight: 0.9, kinds: ['repair', 'shield', 'mine'] },
    { x: 17.5, z: 8.5, weight: 0.9, kinds: ['repair', 'shield', 'mine'] },
  ],
  hazards: [
    // 中轴 spark（自对称）：边路走廊汇入中轴的要道，制造路线抉择
    { x: 0, z: -12.9, radius: 2.3, kind: 'spark', strength: 1 },
    { x: 0, z: 12.9, radius: 2.3, kind: 'spark', strength: 1 },
    // 中路 mud（镜像对）：出生区前缘的减速带，不堵死主路
    { x: -20.5, z: 0, radius: 2.3, kind: 'mud', strength: 0.7 },
    { x: 20.5, z: 0, radius: 2.3, kind: 'mud', strength: 0.7 },
    // 边路走廊 mud（镜像对）：绕后路线的时间成本
    { x: -8, z: -15.5, radius: 2.4, kind: 'mud', strength: 0.72 },
    { x: 8, z: -15.5, radius: 2.4, kind: 'mud', strength: 0.72 },
  ],
  palette: {
    sky: 0x7fd8ff,
    fog: 0xaee9ff,
    groundA: '#8acb6f',
    groundB: '#9edb78',
    groundLine: '#d9f3a7',
    rim: 0xffdf3d,
    accent: 0x35f0d8,
    danger: 0xff6d58,
  },
  scenery: 'meadow',
};
